<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatMoneyWithSymbol } from '@/lib/money'
import { sideConfig } from '@/lib/product'
import SideHeading from './SideHeading.vue'
import ListingsBadge from './ListingsBadge.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ExternalLink, LoaderCircle, ChevronDown } from 'lucide-vue-next'
import FilterChips, { type ChipFilter } from '@/components/FilterChips.vue'
import type { OnlineItem } from './catalogDetailTypes'

const { t } = useI18n()

const props = defineProps<{
    side: 'resell' | 'buyback'
    items: OnlineItem[]
    /** Of the current narrowing — the badge counts what the list would page through. */
    total: number
    loading: boolean
    loadingMore: boolean
    hasMore: boolean
    /** Every value the unnarrowed listing holds, so a selection can be widened again. */
    countries: string[]
    currencies: string[]
    country: string | null
    currency: string | null
}>()

const emit = defineEmits<{
    (event: 'update:filters', value: { country: string | null; currency: string | null }): void
    (event: 'load-more'): void
}>()

const accent = computed(() => sideConfig[props.side].accent)

const filterGroups = computed(() => [
    { kind: 'currency', label: t('catalog.details.competitorPrices.currency'), options: props.currencies },
    {
        kind: 'country',
        label: t('catalog.details.competitorPrices.country'),
        options: props.countries,
        uppercase: true,
    },
])

/**
 * The narrowing lives on the server, so the control is driven by what was last asked
 * for rather than by state of its own — a request that fails leaves the chips showing
 * the listing actually on screen.
 */
const activeFilter = computed<ChipFilter | null>(() => {
    if (props.currency !== null) {
        return { kind: 'currency', value: props.currency }
    }

    if (props.country !== null) {
        return { kind: 'country', value: props.country }
    }

    return null
})

/** One event carrying both dimensions, so switching between them is one request. */
const narrow = (filter: ChipFilter | null) =>
    emit('update:filters', {
        currency: filter?.kind === 'currency' ? filter.value : null,
        country: filter?.kind === 'country' ? filter.value : null,
    })

/**
 * One table per currency, in the order the server returned them.
 *
 * The list is ranked by price and paged, so a single table spanning two currencies
 * ranks CHF 238 above EUR 253 — a comparison between different units. The server
 * orders by currency first, which keeps each currency's rows contiguous as pages are
 * appended, so the grouping here is a walk over the rows rather than a re-sort.
 *
 * Partitioning rather than picking one: ADR-022. A currency the account trades in is
 * never off screen, it just has its own block.
 */
const currencyGroups = computed(() => {
    const groups = new Map<string, OnlineItem[]>()

    for (const item of props.items) {
        const group = groups.get(item.currency)

        if (group) {
            group.push(item)
            continue
        }

        groups.set(item.currency, [item])
    }

    return [...groups.entries()].map(([currency, items]) => ({ currency, items }))
})

/** A single-currency listing is the whole card, so it needs no block heading. */
const isPartitioned = computed(() => currencyGroups.value.length > 1)

const title = computed(() =>
    props.side === 'resell' ? t('catalog.details.onlineItems.resell') : t('catalog.details.onlineItems.tradein'),
)
</script>

<template>
    <Card class="border-l-4" :class="accent.border">
        <!-- Headed exactly as the history card above it: same case, same weight, same rule. -->
        <CardHeader class="py-4">
            <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
                <SideHeading :side="side" :title="title" size="sm" class="w-full">
                    <ListingsBadge :value="total > 0 ? total : null" />
                </SideHeading>
            </div>

            <!-- Says what the card is a list *of*, so it belongs with the title rather
                 than above the first row as a note on the table. -->
            <CardDescription class="text-xs">
                {{ t('catalog.details.onlineItems.inStock') }}
            </CardDescription>

            <!--
                Under the title, because it narrows the whole card. The narrowing is sent
                to the server rather than applied to the rows on screen: the listing is
                paged, so filtering here would hide rows the next page then brings back.
            -->
            <FilterChips :model-value="activeFilter" :groups="filterGroups" class="mt-3" @update:model-value="narrow" />
        </CardHeader>

        <CardContent>
            <div v-if="loading" class="space-y-3">
                <Skeleton class="h-8 w-full" />
                <Skeleton class="h-8 w-full" />
                <Skeleton class="h-8 w-full" />
            </div>

            <p v-else-if="items.length === 0" class="text-sm text-muted-foreground">
                {{ t('catalog.details.onlineItems.empty') }}
            </p>

            <template v-else>
                <div
                    v-for="group in currencyGroups"
                    :key="group.currency"
                    :class="isPartitioned ? 'mt-6 first:mt-0' : ''"
                >
                    <!--
                        Named only when there is more than one: with a single currency the
                        symbol in every price already says it, and a heading over the whole
                        card would state what nothing contradicts.
                    -->
                    <p
                        v-if="isPartitioned"
                        class="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                        {{ group.currency }}
                    </p>

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{{ t('catalog.details.table.headings.competitor') }}</TableHead>
                                <TableHead>{{ t('catalog.details.table.headings.country') }}</TableHead>
                                <TableHead>{{ t('catalog.details.table.headings.price') }}</TableHead>
                                <TableHead>{{ t('catalog.details.table.headings.color') }}</TableHead>
                                <TableHead v-if="side === 'resell'">{{
                                    t('catalog.details.table.headings.link')
                                }}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow v-for="item in group.items" :key="item.id">
                                <TableCell>
                                    <div class="text-sm">{{ item.vendor.name }}</div>
                                    <div v-if="item.seller_name" class="text-xs text-muted-foreground">
                                        {{ item.seller_name }}
                                    </div>
                                </TableCell>
                                <TableCell class="uppercase">{{ item.country ?? item.vendor.country }}</TableCell>
                                <TableCell class="font-medium tabular-nums">{{
                                    formatMoneyWithSymbol(item.price, item.currency_symbol)
                                }}</TableCell>
                                <TableCell class="capitalize">{{ item.color ?? '—' }}</TableCell>
                                <TableCell v-if="side === 'resell'">
                                    <a
                                        v-if="item.url"
                                        :href="item.url"
                                        target="_blank"
                                        class="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <ExternalLink class="size-3.5" />
                                    </a>
                                    <span v-else class="text-muted-foreground">—</span>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>

                <div v-if="hasMore" class="mt-4 flex justify-center">
                    <Button variant="outline" size="sm" :disabled="loadingMore" @click="emit('load-more')">
                        <LoaderCircle v-if="loadingMore" class="size-3.5 animate-spin" />
                        <ChevronDown v-else class="size-3.5" />
                        {{ t('catalog.details.onlineItems.showMore') }}
                    </Button>
                </div>
            </template>
        </CardContent>
    </Card>
</template>
